import React from "react";

import "./demoPage.css";
import "./themes/ascetic.css";

import Disapearable from "./lib/disapearable";
import { Html } from "./lib/html";
import { CodesandboxBlock } from "./example";
import AnimatedInput from "./examples/AnimatedInput/component";
import PhoneNumberDemo from "./examples/PhoneNumber/demo";
import HighlightErrorDemo from "./examples/HighlightError/demo";
import NumberGroupsDemo from "./examples/NumberGroups/demo";
import MessageInputDemo from "./examples/MessageInput/demo";
import FixedInputValueDemo from "./examples/FixedInputValue/demo";

import slogan from "./slogan";

export default function DemoPage() {
  return (
    <>
      <MainScreen />
      <main className="container">
        <AboutBlock />
        <Examples />
      </main>
      <div className="endSpace" />
      <footer>
        <div className="container">
          &copy; <a href="https://rumk.in">Rumkin</a>, 2019.
        </div>
      </footer>
    </>
  );
}

function MainScreen() {
  return (
    <div className="mainPage">
      <div className="mainPage-head">
        <a href="https://github.com/rumkin/mighty-input">
          <img
            height="24"
            width="24"
            alt="github icon"
            src="https://unpkg.com/simple-icons@latest/icons/github.svg"
          />
        </a>
      </div>
      <section className="mainPage-body">
        <div className="mainPage-content">
          <hgroup className="mainPage-intro">
            <h1>{"<MightyInput>"}</h1>
            <h3>{slogan}</h3>
          </hgroup>
          <AnimatedInput
            value={"Hello, World \ud83d\ude00\ud83d\udc97"}
            autoFocus
          />
          <p className="mainPage-desc">
            Tiny React component (only 10 Kb) for powerful text input.
            <br />
            Current version is <code>0.1.7</code>.
          </p>
        </div>
      </section>
      <p style={{ textAlign: "center", fontSize: "12px" }}>
        ️Source code of the example.
      </p>
      <div className="container">
        <CodesandboxBlock
          iconSize="18"
          filepath="/src/examples/AnimatedInput.js"
        >
          <Html
            as="pre"
            className="sourceCode language-js"
            code={`<code class="  language-jsx"><span class="token keyword">function</span> <span class="token function">AnimatedInput</span><span class="token punctuation">(</span><span class="token parameter"><span class="token punctuation">{</span> value<span class="token punctuation">,</span> <span class="token operator">...</span>props <span class="token punctuation">}</span></span><span class="token punctuation">)</span> <span class="token punctuation">{</span>
  <span class="token keyword">const</span> <span class="token function-variable function">render</span> <span class="token operator">=</span> <span class="token parameter">nextValue</span> <span class="token operator">=&gt;</span> Array<span class="token punctuation">.</span><span class="token function">from</span><span class="token punctuation">(</span>nextValue<span class="token punctuation">)</span>
  <span class="token punctuation">.</span><span class="token function">map</span><span class="token punctuation">(</span><span class="token punctuation">(</span><span class="token parameter">char<span class="token punctuation">,</span> i</span><span class="token punctuation">)</span> <span class="token operator">=&gt;</span> <span class="token punctuation">(</span>
    <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>span</span> <span class="token attr-name">key</span><span class="token script language-javascript"><span class="token script-punctuation punctuation">=</span><span class="token punctuation">{</span>i<span class="token punctuation">}</span></span> <span class="token attr-name">className</span><span class="token script language-javascript"><span class="token script-punctuation punctuation">=</span><span class="token punctuation">{</span><span class="token template-string"><span class="token string">\`animation-</span><span class="token interpolation"><span class="token interpolation-punctuation punctuation">\${</span><span class="token function">getCharType</span><span class="token punctuation">(</span>char<span class="token punctuation">)</span><span class="token interpolation-punctuation punctuation">}</span></span><span class="token string">\`</span></span><span class="token punctuation">}</span></span><span class="token punctuation">&gt;</span></span><span class="token plain-text">
      </span><span class="token punctuation">{</span>char<span class="token punctuation">}</span><span class="token plain-text">
    </span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>span</span><span class="token punctuation">&gt;</span></span>
  <span class="token punctuation">)</span><span class="token punctuation">)</span><span class="token punctuation">;</span>

  <span class="token keyword">return</span> <span class="token punctuation">(</span>
    <span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span><span class="token class-name">MightyInput</span></span> <span class="token attr-name">value</span><span class="token script language-javascript"><span class="token script-punctuation punctuation">=</span><span class="token punctuation">{</span>value<span class="token punctuation">}</span></span> <span class="token attr-name">render</span><span class="token script language-javascript"><span class="token script-punctuation punctuation">=</span><span class="token punctuation">{</span>render<span class="token punctuation">}</span></span> <span class="token spread"><span class="token punctuation">{</span><span class="token punctuation">...</span><span class="token attr-value">props</span><span class="token punctuation">}</span></span><span class="token punctuation">/&gt;</span></span>
  <span class="token punctuation">)</span><span class="token punctuation">;</span>
<span class="token punctuation">}</span>

<span class="token keyword">function</span> <span class="token function">getCharType</span><span class="token punctuation">(</span><span class="token parameter">char<span class="token punctuation">,</span> index</span><span class="token punctuation">)</span> <span class="token punctuation">{</span>
  <span class="token keyword">switch</span> <span class="token punctuation">(</span>char<span class="token punctuation">)</span> <span class="token punctuation">{</span>
    <span class="token keyword">case</span> <span class="token string">"\ud83d\ude00"</span><span class="token punctuation">:</span> <span class="token comment">// Smiley face emoji</span>
      <span class="token keyword">return</span> <span class="token string">"smiley"</span><span class="token punctuation">;</span>
    <span class="token keyword">case</span> <span class="token string">"\ud83d\udc97"</span><span class="token punctuation">:</span> <span class="token comment">// Heart emoji</span>
      <span class="token keyword">return</span> <span class="token string">"heart"</span><span class="token punctuation">;</span>
    <span class="token keyword">default</span><span class="token punctuation">:</span>
      <span class="token keyword">return</span> <span class="token string">"char"</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>
<span class="token punctuation">}</span></code>`}
          />
        </CodesandboxBlock>
        <details>
          <summary>CSS</summary>
          <Html
            as="pre"
            className="sourceCode"
            code={`<code class=" language-css"><span class="token selector">.animation-char</span> <span class="token punctuation">{</span>
  <span class="token property">display</span><span class="token punctuation">:</span> inline-block<span class="token punctuation">;</span>
  <span class="token property">animation</span><span class="token punctuation">:</span> raise 1s 0s linear<span class="token punctuation">;</span>
<span class="token punctuation">}</span>

<span class="token selector">.animation-smiley</span> <span class="token punctuation">{</span>
  <span class="token property">display</span><span class="token punctuation">:</span> inline-block<span class="token punctuation">;</span>
  <span class="token property">animation</span><span class="token punctuation">:</span> raise 1s 0s linear<span class="token punctuation">,</span> spin 5s 0s linear infinite<span class="token punctuation">;</span>
  <span class="token property">transform-origin</span><span class="token punctuation">:</span> 40% 45%<span class="token punctuation">;</span>
<span class="token punctuation">}</span>

<span class="token selector">.animation-heart</span> <span class="token punctuation">{</span>
  <span class="token property">display</span><span class="token punctuation">:</span> inline-block<span class="token punctuation">;</span>
  <span class="token property">animation</span><span class="token punctuation">:</span> raise 1s 0s linear<span class="token punctuation">,</span> heartbeat 1s 0s linear infinite<span class="token punctuation">;</span>
<span class="token punctuation">}</span>

<span class="token atrule"><span class="token rule">@keyframes</span> raise</span> <span class="token punctuation">{</span>
  <span class="token selector">from</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>0.3<span class="token punctuation">)</span><span class="token punctuation">;</span>
    <span class="token property">opacity</span><span class="token punctuation">:</span> 0.2<span class="token punctuation">;</span>
  <span class="token punctuation">}</span>

  <span class="token selector">to</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>1<span class="token punctuation">)</span><span class="token punctuation">;</span>
    <span class="token property">opacity</span><span class="token punctuation">:</span> 1<span class="token punctuation">;</span>
  <span class="token punctuation">}</span>
<span class="token punctuation">}</span>

<span class="token atrule"><span class="token rule">@keyframes</span> spin</span> <span class="token punctuation">{</span>
  <span class="token selector">from</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">rotate</span><span class="token punctuation">(</span>0deg<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>

  <span class="token selector">to</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">rotate</span><span class="token punctuation">(</span>360deg<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>
<span class="token punctuation">}</span>

<span class="token atrule"><span class="token rule">@keyframes</span> heartbeat</span> <span class="token punctuation">{</span>
  <span class="token selector">from</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>1<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>

  <span class="token selector">25%</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>1.25<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>

  <span class="token selector">26%</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>1<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>

  <span class="token selector">50%</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>1.25<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>

  <span class="token selector">51%,
  to</span> <span class="token punctuation">{</span>
    <span class="token property">transform</span><span class="token punctuation">:</span> <span class="token function">scale</span><span class="token punctuation">(</span>1<span class="token punctuation">)</span><span class="token punctuation">;</span>
  <span class="token punctuation">}</span>
<span class="token punctuation">}</span></code>`}
          />
        </details>
      </div>
      <div className="mainPage-footnote">
        <Disapearable>
          More examples below <span aria-label="Scroll down">👇</span>
        </Disapearable>
      </div>
    </div>
  );
}

function AboutBlock() {
  return (
    <div className="aboutBlock">
      <article className="aboutBlock-content">
        <h2>About</h2>
        <p>At first a few facts about Mighty Input:</p>
        <ul>
          <li>
            <strong>Regular input element</strong>. It's just an{" "}
            <code>{"<input/>"}</code>. Not a div or span with contenteditable
            mode turned on. That's why it works in any browser without
            artifacts.
          </li>
          <li>
            <strong>HTML instead of plain text</strong>. Mighty input allow to
            replace plain text of field input with HTML. The only rule: you
            should not change text length.
          </li>
          <li>
            <strong>Well known</strong>. Mighty Input uses pretty old trick with
            transparent text and overlapped <code>{"<span/>"}</code>. It has
            been used by Facebook and Google before 2014.
          </li>
          <li>
            <strong>Still a prototype</strong>. This is a late prototype so
            breaking changes are highly possible.
          </li>
        </ul>
      </article>
    </div>
  );
}

function Examples() {
  return (
    <>
      <MessageInputDemo />
      <HighlightErrorDemo />
      <PhoneNumberDemo />
      <NumberGroupsDemo />
      <FixedInputValueDemo />
    </>
  );
}
